import laravelApi from "./api";
import { AuthError, APIError } from "./error_service";

export interface SSOImpersonation {
  is_impersonating: boolean;
  impersonator: {
    id: number;
    name: string;
    email: string;
  } | null;
  leave_url: string;
}

export interface SSOAuthResponse {
  token: string;
  user: {
    id: number;
    name: string;
    email: string;
    is_admin: boolean;
    netsuite_employee_id: number | null;
    effective_rep_ids: number[];
  };
  apps: string[];
  roles: Record<string, string[]>;
  impersonation?: SSOImpersonation;
}

export interface SSOSessionResponse {
  authenticated: boolean;
  user?: {
    id: number;
    name: string;
    email: string;
    is_admin: boolean;
  };
  apps?: string[];
  roles?: Record<string, string[]>;
}

export default class AuthService {
  public getLoginUrl(): string {
    const callback = `${process.env.VUE_APP_URL}/auth/callback`;
    return `${process.env.VUE_APP_API_LARAVEL_URL}/auth/login?redirect=${encodeURIComponent(callback)}&app=${process.env.VUE_APP_SSO_APP_SLUG}`;
  }

  public async exchangeCode(code: string): Promise<SSOAuthResponse> {
    try {
      const res = await laravelApi.post("/api/auth/exchange", {
        code,
        app_slug: process.env.VUE_APP_SSO_APP_SLUG
      });
      return res.data;
    } catch (err) {
      if (err.response && err.response.status === 401) {
        throw new AuthError(
          err.response.data?.message || "Invalid or expired authorization code.",
          401
        );
      }
      throw new APIError(
        err.response?.data?.message || "Authentication failed.",
        err.response?.status || 500
      );
    }
  }

  public async checkAuth(): Promise<SSOSessionResponse> {
    try {
      const res = await laravelApi.get("/api/auth/session");
      return res.data;
    } catch (err) {
      throw new AuthError(
        err.response?.data?.message || "Session validation failed.",
        err.response?.status || 401
      );
    }
  }

  public async logout(): Promise<void> {
    try {
      await laravelApi.post("/api/auth/logout");
    } catch (err) {
      // Ignore logout errors — token may already be invalid
    }
  }
}
